import { FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ThemeToggle } from "../components/ThemeToggle";

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { status, login, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If we're already authenticated, bounce to the intended destination.
  if (ready && status?.current_user != null) {
    const to = (location.state as LocationState | null)?.from || "/overview";
    return <Navigate to={to} replace />;
  }

  // RequireAuth handles the broader redirect chain; these short-circuits
  // just keep the login form from accepting credentials it can't use.
  if (ready && status && !status.jwt_configured) {
    return <Navigate to="/auth/disabled" replace />;
  }
  if (ready && status && status.user_count === 0) {
    return <Navigate to="/setup" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const err = await login(email.trim(), password);
    setBusy(false);
    if (err) {
      setError(err);
      return;
    }
    const to = (location.state as LocationState | null)?.from || "/overview";
    navigate(to, { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4">
      {/* Discreet theme toggle in the corner so the user can match their OS
          before they're inside the portal. */}
      <div className="absolute right-4 top-4">
        <ThemeToggle compact />
      </div>

      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-7 shadow-card dark:shadow-card-dark">
        <div className="mb-6 flex flex-col items-center text-center">
          <BrandMark />
          <h1 className="mt-3 font-mono text-2xl font-bold tracking-tight text-fg">
            FX-Dash
          </h1>
          <p className="mt-1 text-xs text-fg-muted">Sign in to continue</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="username"
            autoFocus
          />
          <Field
            id="password"
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />
          {error && (
            <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-wait disabled:opacity-60"
          >
            {busy ? "signing in…" : "sign in"}
          </button>
        </form>

        <p className="mt-5 text-center text-[10px] uppercase tracking-wider text-fg-muted">
          single-tenant trading dashboard
        </p>
      </div>
    </div>
  );
}

// ---- helpers ---------------------------------------------------------

interface FieldProps {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  autoFocus?: boolean;
  minLength?: number;
}

function Field({
  id,
  label,
  type,
  value,
  onChange,
  autoComplete,
  autoFocus,
  minLength,
}: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className="text-[11px] font-medium uppercase tracking-wider text-fg-muted"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        minLength={minLength}
        className="rounded-md border border-border bg-page px-3 py-2 font-mono text-sm text-fg outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
      />
    </div>
  );
}

export function BrandMark() {
  // Small inline brand mark — a stylised line-chart in the primary color.
  // Stays single-color so it themes correctly without per-mode swaps.
  return (
    <span
      aria-hidden="true"
      className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-white"
    >
      <svg
        viewBox="0 0 24 24"
        className="h-7 w-7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 17 L10 10 L14 14 L21 5" />
      </svg>
    </span>
  );
}
